import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Loader2, CreditCard, Lock, ShieldCheck, CheckCircle2, AlertCircle, Info, Nfc, Coins } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function CheckoutPage() {
    const { planId } = useParams();
    const navigate = useNavigate();
    
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    // Form State'leri
    const [cardName, setCardName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [cvv, setCvv] = useState('');
    
    // Kart Dönme (Flip) Animasyonu İçin
    const [isCvvFocused, setIsCvvFocused] = useState(false);

    useEffect(() => {
        const fetchPlanDetails = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/plans/${planId}`);
                setPlan(response.data);
            } catch (err) {
                console.error("Plan detayları çekilemedi:", err);
                setError("Seçtiğiniz plan bulunamadı veya satışa kapalı.");
            } finally {
                setLoading(false);
            }
        };

        if (planId) fetchPlanDetails();
    }, [planId]);

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await axios.post(`${API_URL}/api/payment/checkout`, {
                plan_id: plan.id,
                card_name: cardName,
                card_number: cardNumber.replace(/\s/g, ''), // Boşlukları silip gönder
                expiry_date: expiryDate,
                cvv: cvv
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert(`Tebrikler! ${plan.name} paketiniz onaylandı. İşlem No: ${response.data.transaction_id}`);
            navigate('/profile');
            
        } catch (err) {
            setError(err.response?.data?.detail || "Ödeme işlemi sırasında bir hata oluştu.");
        } finally {
            setProcessing(false);
        }
    };

    // Kart numarası formatlayıcı (Örn: 1234 5678 1234 5678)
    const formatCardNumber = (val) => {
        return val.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
    };

    // SKT formatlayıcı (Örn: 12/25)
    const formatExpiry = (val) => {
        return val.replace(/\D/g, '').replace(/^(\d{2})/, '$1/').substring(0, 5);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
                <Loader2 className="animate-spin w-12 h-12 text-indigo-500 mb-4" />
                <p className="text-slate-400 font-medium">Ödeme altyapısı hazırlanıyor...</p>
            </div>
        );
    }

    if (error || !plan) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
                <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl max-w-md text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Hata Oluştu</h2>
                    <p className="text-slate-400 mb-6">{error}</p>
                    <button onClick={() => navigate('/pricing')} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all">Paketlere Dön</button>
                </div>
            </div>
        );
    }

    // Finansal Hesaplamalar (KDV %20)
    const taxRate = 0.20;
    const subtotal = plan.price;
    const taxAmount = subtotal * taxRate;
    const totalAmount = subtotal + taxAmount;

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 font-sans pb-24 relative overflow-hidden">
            
            {/* Arkaplan Efektleri */}
            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="max-w-6xl mx-auto px-6 pt-12 relative z-10">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8">
                    <ArrowLeft size={18} /> Geri Dön
                </button>

                <div className="flex items-center gap-3 mb-10">
                    <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20">
                        <Lock className="text-white" size={24} />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-white">Güvenli Ödeme</h1>
                        <p className="text-slate-400">256-bit SSL şifreleme ile güvendesiniz.</p>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-10">
                    
                    {/* SOL TARAF: KART ANİMASYONU VE FORM */}
                    <div className="w-full lg:w-3/5">
                        
                        {/* 🚨 INTERAKTİF 3D KREDİ KARTI ANİMASYONU 🚨 */}
                        <div className="perspective-1000 mb-8 max-w-[400px] mx-auto lg:mx-0 h-[240px]">
                            <motion.div
                                animate={{ rotateY: isCvvFocused ? 180 : 0 }}
                                transition={{ duration: 0.6, type: "spring", stiffness: 260, damping: 20 }}
                                className="w-full h-full relative"
                                style={{ transformStyle: "preserve-3d" }}
                            >
                                {/* KART ÖN YÜZÜ */}
                                <div 
                                    className="absolute inset-0 w-full h-full rounded-[2rem] p-6 flex flex-col justify-between shadow-2xl overflow-hidden bg-gradient-to-tr from-indigo-900 via-indigo-600 to-purple-600 border border-white/10"
                                    style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
                                >
                                    {/* Dekoratif Çizgiler */}
                                    <div className="absolute -right-10 -top-10 w-40 h-40 border-2 border-white/10 rounded-full opacity-50"></div>
                                    <div className="absolute -right-4 -top-4 w-40 h-40 border-2 border-white/10 rounded-full opacity-30"></div>
                                    
                                    <div className="flex justify-between items-start relative z-10">
                                        {/* Çip ve Temassız İkonu */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-9 bg-gradient-to-br from-yellow-200 via-yellow-400 to-yellow-600 rounded-md shadow-sm border border-yellow-500/50 flex items-center justify-center opacity-90">
                                                <div className="w-8 h-5 border border-yellow-700/30 rounded-sm"></div>
                                            </div>
                                            <Nfc size={24} className="text-white/70" />
                                        </div>
                                        <h3 className="font-black text-white/80 italic tracking-widest text-lg">Mekânname</h3>
                                    </div>

                                    <div className="relative z-10 mt-6">
                                        {/* Kart Numarası */}
                                        <p className="text-white text-2xl tracking-[0.15em] font-mono mb-4 drop-shadow-md min-h-[32px]">
                                            {cardNumber ? cardNumber : '•••• •••• •••• ••••'}
                                        </p>

                                        <div className="flex justify-between items-end">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-white/60 uppercase tracking-widest font-bold">Kart Sahibi</span>
                                                <span className="text-white font-bold tracking-widest uppercase text-sm truncate max-w-[200px] min-h-[20px]">
                                                    {cardName || 'AD SOYAD'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <span className="text-[10px] text-white/60 uppercase tracking-widest font-bold">Son Kul.</span>
                                                <span className="text-white font-bold tracking-widest font-mono text-sm min-h-[20px]">
                                                    {expiryDate || 'AA/YY'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* KART ARKA YÜZÜ */}
                                <div 
                                    className="absolute inset-0 w-full h-full rounded-[2rem] flex flex-col justify-center shadow-2xl overflow-hidden bg-gradient-to-tr from-indigo-900 via-indigo-600 to-purple-600 border border-white/10"
                                    style={{ 
                                        backfaceVisibility: "hidden", 
                                        WebkitBackfaceVisibility: "hidden",
                                        transform: "rotateY(180deg)" 
                                    }}
                                >
                                    {/* Manyetik Şerit */}
                                    <div className="w-full h-12 bg-black/80 absolute top-8"></div>
                                    
                                    {/* CVV Alanı */}
                                    <div className="w-full px-6 mt-4">
                                        <p className="text-right text-[10px] text-white/80 font-bold mb-1 mr-2 tracking-wider uppercase">Güvenlik Kodu (CVV)</p>
                                        <div className="w-full h-10 bg-white/90 rounded flex items-center justify-end px-3">
                                            <span className="text-slate-800 font-mono italic font-bold tracking-widest">{cvv || '•••'}</span>
                                        </div>
                                    </div>

                                    <div className="absolute bottom-6 w-full px-6 text-[8px] text-white/40 text-center leading-tight">
                                        Bu kart Mekânname AI sistemleri için güvenli bir şekilde oluşturulmuş simüle edilmiş bir ödeme aracıdır.
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                        {/* KART ANİMASYONU BİTİŞ */}

                        <form onSubmit={handlePaymentSubmit} className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                <CreditCard className="text-indigo-400" size={20} /> Bilgileri Girin
                            </h3>

                            <div className="space-y-5">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest">Kart Üzerindeki İsim</label>
                                    <input 
                                        type="text" required
                                        value={cardName} onChange={(e) => setCardName(e.target.value.toUpperCase())}
                                        onFocus={() => setIsCvvFocused(false)}
                                        placeholder="AD SOYAD"
                                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest">Kart Numarası</label>
                                    <div className="relative">
                                        <input 
                                            type="text" required maxLength="19"
                                            value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                                            onFocus={() => setIsCvvFocused(false)}
                                            placeholder="0000 0000 0000 0000"
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-12 pr-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono text-lg tracking-wider"
                                        />
                                        <CreditCard className="absolute left-4 top-4 text-slate-500" size={20} />
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest">Son Kul. Tarihi</label>
                                        <input 
                                            type="text" required maxLength="5"
                                            value={expiryDate} onChange={(e) => setExpiryDate(formatExpiry(e.target.value))}
                                            onFocus={() => setIsCvvFocused(false)}
                                            placeholder="AA/YY"
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono text-center text-lg"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-widest">CVC</label>
                                        <input 
                                            type="text" required maxLength="3"
                                            value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                                            onFocus={() => setIsCvvFocused(true)} // Tıklanınca kartı çevirir
                                            onBlur={() => setIsCvvFocused(false)}  // Çıkınca eski haline döner
                                            placeholder="123"
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-mono text-center text-lg"
                                        />
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={processing}
                                    className={`w-full mt-6 flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-lg transition-all shadow-lg active:scale-95
                                    ${processing ? 'bg-indigo-600/50 cursor-not-allowed text-white/70' : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/30'}`}
                                >
                                    {processing ? <Loader2 className="animate-spin w-6 h-6" /> : <ShieldCheck className="w-6 h-6" />}
                                    {processing ? 'İşleminiz Yapılıyor...' : `₺${totalAmount.toFixed(2)} Öde`}
                                </button>
                            </div>

                            <div className="mt-6 flex flex-col items-center justify-center gap-1 text-xs text-slate-500 font-medium">
                                <div className="flex items-center gap-2"><Lock size={14} /> Kart bilgileriniz sistemlerimizde saklanmaz.</div>
                                <div>* Fiyatlarımıza KDV dahil değildir.</div>
                            </div>
                        </form>
                    </div>

                    {/* SAĞ TARAF: SİPARİŞ ÖZETİ */}
                    <div className="w-full lg:w-2/5">
                        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 sticky top-8 shadow-xl">
                            <h3 className="text-xl font-bold text-white mb-6 border-b border-slate-800 pb-4">Sipariş Özeti</h3>
                            
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <p className="font-bold text-lg text-white mb-1">{plan.name}</p>
                                    <p className="text-sm text-slate-400">{plan.is_recurring ? 'Aylık Abonelik' : 'Tek Seferlik Yükleme'}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-lg text-white">₺{subtotal.toFixed(2)}</p>
                                </div>
                            </div>

                            <div className="bg-slate-950 rounded-2xl p-4 mb-6 flex items-center gap-4 border border-slate-800">
                                <div className="bg-amber-500/20 p-3 rounded-xl">
                                    <Coins className="text-amber-500 w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Kazanılacak</p>
                                    <p className="text-lg font-black text-amber-400">{plan.token_amount} Token</p>
                                </div>
                            </div>

                            <div className="space-y-3 text-sm border-b border-slate-800 pb-6 mb-6">
                                <div className="flex justify-between text-slate-300">
                                    <span>Ara Toplam</span>
                                    <span>₺{subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-400">
                                    <span>KDV (%20)</span>
                                    <span>₺{taxAmount.toFixed(2)}</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-end mb-6">
                                <span className="text-lg font-bold text-white">Genel Toplam</span>
                                <div className="text-right">
                                    <span className="text-3xl font-black text-white">₺{totalAmount.toFixed(2)}</span>
                                </div>
                            </div>

                            <ul className="space-y-2 text-sm text-slate-400">
                                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-500" /> Anında aktivasyon</li>
                                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-500" /> Kolay iptal imkanı</li>
                            </ul>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}